import type { ComponentPropsWithoutRef, ComponentRef, HTMLAttributes, Ref } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'

import cn from '@components/utils/cn'
import Icon from '@components/utils/Icon'
import { preventEscapeForOpenPopup } from '@components/utils/preventEscapeForOpenPopup'
import { scopedTranslation } from '@utils/I18n'

const t = scopedTranslation('shared')

const Root: typeof DialogPrimitive.Root = DialogPrimitive.Root

const Trigger: typeof DialogPrimitive.Trigger = DialogPrimitive.Trigger

const Portal = ({ children, ...props }: DialogPrimitive.DialogPortalProps) => (
  <DialogPrimitive.Portal {...props}>
    {/*
      The `tailwind` class applies the Tailwind reset to elements
      within the Portal (e.g. to the close button). Otherwise the
      default browser styles are applied given the portal appears
      at the root of the document (near the closing </body> tag),
      rather than displaying within the main root #app element
    */}
    <div className="tailwind">{children}</div>
  </DialogPrimitive.Portal>
)
Portal.displayName = DialogPrimitive.Portal.displayName

const Close: typeof DialogPrimitive.Close = DialogPrimitive.Close

const Overlay = ({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & {
  ref?: Ref<ComponentRef<typeof DialogPrimitive.Overlay>>
}) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'tw:fixed tw:inset-0 tw:z-50 tw:bg-black/60 tw:data-[state=open]:animate-in tw:data-[state=closed]:animate-out tw:data-[state=closed]:fade-out-0 tw:data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
)

export type DialogSize = 'lg' | 'xl' | '2xl' | '3xl' | '4xl'
export type DialogPosition = 'middle' | 'top'
type DialogContentProps = {
  size?: DialogSize
  position?: DialogPosition
  scroll?: boolean
}
const Content = ({
  className,
  children,
  size = 'lg',
  position = 'middle',
  scroll = false,
  onEscapeKeyDown,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof DialogPrimitive.Content> &
  DialogContentProps & {
    ref?: Ref<ComponentRef<typeof DialogPrimitive.Content>>
  }) => (
  <Portal>
    <Overlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        size === 'lg' && 'tw:max-w-lg',
        size === 'xl' && 'tw:max-w-xl',
        size === '2xl' && 'tw:max-w-2xl',
        size === '3xl' && 'tw:max-w-3xl',
        size === '4xl' && 'tw:max-w-4xl',
        position === 'middle' && 'tw:top-1/2 tw:-translate-y-1/2',
        position === 'top' && 'tw:top-0 tw:md:top-[7.5vh]',
        scroll && 'tw:overflow-y-auto',
        'tw:bg-white tw:fixed tw:left-1/2 tw:z-50 tw:grid tw:grid-cols-1 tw:w-full tw:-translate-x-1/2 tw:gap-4 tw:border tw:p-6 tw:shadow-lg tw:duration-200 tw:data-[state=open]:animate-in tw:data-[state=closed]:animate-out tw:data-[state=closed]:fade-out-0 tw:data-[state=open]:fade-in-0 tw:data-[state=closed]:zoom-out-95 tw:data-[state=open]:zoom-in-95 tw:sm:rounded-lg',
        'tw:max-h-screen tw:md:max-h-[85vh]',
        className
      )}
      onEscapeKeyDown={(event) => {
        preventEscapeForOpenPopup(event)
        onEscapeKeyDown?.(event)
      }}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="tw:p-2 tw:absolute tw:right-2.5 tw:top-2.5 tw:rounded-xs tw:opacity-70 tw:transition-opacity tw:hover:opacity-100 tw:focus:outline-hidden tw:focus:ring-2 tw:focus:ring-primary-500 tw:focus:ring-offset-2 tw:disabled:pointer-events-none">
        <Icon type="x-close" height={10} width={10} className="tw:h-2.5 tw:w-2.5" />
        <span className="tw:sr-only">{t('buttons.close')}</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </Portal>
)

const Header = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('tw:flex tw:flex-col tw:space-y-1.5 tw:text-center tw:sm:text-left', className)} {...props} />
)
Header.displayName = 'DialogHeader'

const Footer = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('tw:flex tw:flex-col-reverse tw:sm:flex-row tw:sm:justify-end tw:sm:space-x-2', className)}
    {...props}
  />
)
Footer.displayName = 'DialogFooter'

const Title = ({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & {
  ref?: Ref<ComponentRef<typeof DialogPrimitive.Title>>
}) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('tw:text-xl tw:font-semibold tw:leading-none tw:tracking-tight', className)}
    {...props}
  />
)

const Description = ({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & {
  ref?: Ref<ComponentRef<typeof DialogPrimitive.Description>>
}) => <DialogPrimitive.Description ref={ref} className={cn('tw:text-sm', className)} {...props} />

export { Root, Portal, Overlay, Close, Trigger, Content, Header, Footer, Title, Description }
